import { DotsIcon } from '@/assets/icons/icons';
import { TruncateWithTooltip } from '@/components';
import { PlusOutlined } from '@ant-design/icons';
import { Button, Divider, Dropdown, Input, MenuProps, message } from 'antd';
import { useState } from 'react';
import { makeDefaultSheet } from './tabStructure';
import { HeaderItem, TabItem } from '../types';

interface DownloadReportTabsProps {
  tabs?: TabItem[];
  setTabs?: React.Dispatch<React.SetStateAction<TabItem[]>>;
  setActiveTab?: React.Dispatch<React.SetStateAction<string>>;
  activeTab?: string;
  headerList?: HeaderItem[];
  activeTabIndex: number;
  setActiveTabIndex: React.Dispatch<React.SetStateAction<number>>;
  setMultipleTabs: React.Dispatch<React.SetStateAction<TabItem[]>>;
  handleTabChange: (param: number) => void;
  setCanUpdateHeaders: React.Dispatch<React.SetStateAction<boolean>>;
  isTabSwitchRef: React.MutableRefObject<boolean>;
  isAddingTabRef: React.MutableRefObject<boolean>;
}

const DownloadReportTabs: React.FC<DownloadReportTabsProps> = ({
  tabs,
  activeTabIndex,
  setActiveTabIndex,
  setMultipleTabs,
  handleTabChange,
  setCanUpdateHeaders,
  isTabSwitchRef,
  isAddingTabRef,
}) => {
  // -----------------------
  // Add New Tab
  // -----------------------

  const addTab = () => {
    setActiveTabIndex(tabs.length);
    isTabSwitchRef.current = false;
    isAddingTabRef.current = true;
    setCanUpdateHeaders(true);
    setMultipleTabs((prev) => [...prev, makeDefaultSheet(tabs.length)]);
  };

  // -----------------------
  // Rename Tab
  // -----------------------

  const [editingText, setEditingText] = useState('');

  const renameTab = (index: number) => {
    setMultipleTabs((prev) => {
      const old = [...prev];
      old[index] = { ...old[index], editing: true };
      return old;
    });
  };

  const finishRename = (index: number) => {
    const newName = editingText.trim();
    if (!newName) return;

    setMultipleTabs((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        sheet_name: newName,
        editing: false,
      };
      return updated;
    });
  };

  // -----------------------
  // Delete Tab
  // -----------------------
  const deleteTab = (id: number) => {
    const newTabs = tabs.filter((t, i) => i !== id);
    if (newTabs.length === 0) {
      message.error('You cannot delete default tab!');
      return;
    }
    setMultipleTabs(newTabs);
    isTabSwitchRef.current = false;
    if (activeTabIndex === id && newTabs.length > 0) {
      setActiveTabIndex(0);
    }
  };

  // dropdown items for tab
  const getDropdownItems = (id: number, name: string): MenuProps['items'] => [
    {
      key: 'rename',
      label: 'Rename',
      onClick: () => {
        renameTab(id);
        setEditingText(name);
      },
    },
    {
      key: 'delete',
      danger: true,
      label: 'Delete',
      onClick: () => {
        deleteTab(id);
      },
    },
  ];

  return (
    <>
      <div>
        {/* ADD TAB BUTTON */}
        <Divider className='m-0' />
        <div className='tabs-container'>
          <Button
            icon={<PlusOutlined />}
            type='default'
            onClick={addTab}
            style={{ borderRadius: 0, border: 0 }}
          />
          {/* TAB LIST */}
          {tabs?.map((tab, index) => (
            <div
              key={index}
              onClick={() => {
                handleTabChange(index);
              }}
              className={`custom-tab-item ${activeTabIndex === index ? 'active' : ''}`}
            >
              {/*  Rename Tab */}
              {tab?.editing ? (
                <Input
                  className='custom-editable-preset-name-input'
                  autoFocus
                  size='small'
                  value={editingText}
                  onChange={(e) => setEditingText(e.target.value)}
                  onBlur={() => finishRename(index)}
                  style={{ width: 120 }}
                />
              ) : (
                <span>
                  <TruncateWithTooltip
                    text={String(tab?.sheet_name || '')}
                    max={20}
                    render={(t) => (
                      <span style={{ width: 'fit-content' }}>
                        <span>{t}</span>
                      </span>
                    )}
                  />
                </span>
              )}

              {/* Tab Dropdown menu */}
              {!tab?.editing && activeTabIndex === index ? (
                <div onClick={(e) => e.stopPropagation()}>
                  <Dropdown
                    menu={{ items: getDropdownItems(index, tab?.sheet_name) }}
                  >
                    <span className='flex-center y'>
                      <DotsIcon color='var(--text-primary)' />
                    </span>
                  </Dropdown>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default DownloadReportTabs;
